import { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import { buildWhatsAppLink, telHref, BUSINESS_PHONE_DISPLAY } from "../../lib/whatsapp";
import { WhatsAppIcon, Mark } from "./Icons";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/hyderabad-to-srisailam", label: "Hyderabad to Srisailam" },
  { to: "/srisailam-tour-packages", label: "Tour Packages" },
  { to: "/srisailam-sightseeing", label: "Sightseeing" },
  { to: "/srisailam-cab-service", label: "Cab Services" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const location = useLocation();
  const onHome = location.pathname === "/";
  const dark = onHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const phone = BUSINESS_PHONE_DISPLAY;
  const wa = buildWhatsAppLink();

  return (
    <motion.header
      initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled || !onHome ? "bg-white/95 backdrop-blur-xl border-b border-border shadow-sm" : "bg-transparent"}`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo" aria-label="RSK Tours home">
          <Mark className="h-10 sm:h-11 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,"-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${isActive ? "text-gold" : (dark ? "text-white/90 hover:text-gold" : "text-navy hover:text-gold")}`
              }>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {phone && <a href={telHref} data-testid="nav-call" aria-label="Call RSK Tours"
            className={`inline-flex items-center gap-2 h-10 px-4 rounded-full border transition-colors ${dark ? "border-white/40 text-white hover:bg-white/10" : "border-navy/20 text-navy hover:bg-navy/5"}`}>
            <Phone className="w-4 h-4" /> <span className="text-sm">{BUSINESS_PHONE_DISPLAY}</span>
          </a>}
          <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="nav-whatsapp-btn"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-gold hover:bg-gold-hover text-navy-deep text-sm font-medium transition-colors">
            <WhatsAppIcon className="w-4 h-4" /> Book Now
          </a>
        </div>

        <button className={`lg:hidden ${dark ? "text-white" : "text-navy"}`} onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle" aria-label="Toggle navigation">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-border px-4 py-5 flex flex-col gap-1 max-h-[80vh] overflow-y-auto" data-testid="nav-mobile-menu">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"}
              className={({ isActive }) => `py-3 px-2 rounded-md ${isActive ? "text-gold" : "text-navy"}`}>{n.label}</NavLink>
          ))}
          <div className="mt-3 grid grid-cols-2 gap-3">
            {phone && <a href={telHref} className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-navy/20 text-navy"><Phone className="w-4 h-4"/>Call</a>}
            <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-gold text-navy-deep font-medium"><WhatsAppIcon className="w-4 h-4"/>WhatsApp</a>
          </div>
        </div>
      )}
    </motion.header>
  );
}
